import dayjs from "dayjs"
import { getUserAvatarUrl } from "../../../helpers/userAvatar"
import { getUserFullName } from "../../../helpers/userDisplay"

/** Relations for Manage City Calls cards. */
export const CITY_CALL_LIST_WITH = ["user", "categories", "cities", "media"]

export const CITY_CALL_TYPE_OPTIONS = [
  { value: "informal", label: "Informal" },
  { value: "formal", label: "Formal" },
]

export const DEFAULT_CITY_CALL_TYPE = "formal"

export const CITY_CALL_STATUS_TABS = [
  { key: "all", label: "All", countTone: "muted" },
  {
    key: "awaiting",
    label: "Awaiting Approval",
    countTone: "awaiting",
  },
  { key: "approved", label: "Approved", countTone: "approved" },
  { key: "rejected", label: "Rejected", countTone: "rejected" },
]

/** UI tab key → API status query value. */
export const CITY_CALL_TAB_TO_API_STATUS = {
  awaiting: "pending",
  approved: "published",
  rejected: "rejected",
}

const toCount = value => {
  const n = Number(value)
  return Number.isFinite(n) ? n : 0
}

/**
 * Map GET /api/v1/admin/calls/summary → Manage City Calls status tabs.
 */
export const mapCityCallStatusTabs = (summary = {}) => {
  const awaiting = toCount(summary.pending)
  const approved = toCount(summary.published)
  const rejected = toCount(summary.rejected ?? summary.rejected_closed_archived)
  const all =
    toCount(summary.total_requests) || awaiting + approved + rejected

  const counts = {
    all,
    awaiting,
    approved,
    rejected,
  }

  return CITY_CALL_STATUS_TABS.map(tab => ({
    ...tab,
    count: counts[tab.key] ?? 0,
  }))
}

/** API status → UI tab / action key. */
export const cityCallUiStatus = status => {
  const key = String(status || "")
    .trim()
    .toLowerCase()
  if (key === "pending" || key === "draft") return "awaiting"
  if (key === "published" || key === "closed" || key === "archived") {
    return "approved"
  }
  if (key === "rejected") return "rejected"
  return key || "awaiting"
}

const personName = user => {
  if (!user) return "--"
  return getUserFullName(user, { fallback: "--" })
}

const categoryLabel = categories => {
  if (!Array.isArray(categories) || categories.length === 0) return "General"
  return categories.map(cat => cat?.name).filter(Boolean)[0] || "General"
}

const cityLabel = (cities, isGlobal) => {
  if (isGlobal) return "Global"
  if (!Array.isArray(cities) || cities.length === 0) return "--"
  const city = cities[0]
  const parts = [city?.city, city?.state || city?.country].filter(Boolean)
  return parts.join(", ") || "--"
}

const coverImage = item => {
  const media = Array.isArray(item?.media) ? item.media : []
  const first = media[0]
  return (
    first?.path_url ||
    first?.thumbnail_path_url ||
    first?.media_url ||
    item?.cover_image ||
    ""
  )
}

/**
 * Map GET /api/v1/admin/calls item → Manage City call card.
 */
export const mapCityCallRow = item => {
  const user = item?.user || null
  const responses = Number(item?.total_responses)
  const extra = Number.isFinite(responses) ? Math.max(0, responses) : 0

  const isGlobal = Boolean(item?.is_global)

  return {
    id: item?.id,
    org:
      item?.organization ||
      item?.organisation ||
      item?.company ||
      personName(user),
    title: String(item?.title || item?.details || "").trim() || "--",
    description: String(item?.details || item?.description || "").trim() || "--",
    city: cityLabel(item?.cities, isGlobal),
    isGlobal,
    category: categoryLabel(item?.categories),
    status: cityCallUiStatus(item?.status),
    apiStatus: String(item?.status || "")
      .trim()
      .toLowerCase(),
    image: coverImage(item),
    poster: {
      name: personName(user),
      avatar: getUserAvatarUrl(user),
    },
    applicants: [],
    applicantExtra: extra,
    postedAt: item?.published_at || item?.created_at || "",
    postedAtLabel: item?.created_at
      ? dayjs(item.created_at).isValid()
        ? dayjs(item.created_at).format("MMM D, YYYY")
        : "--"
      : "--",
    raw: item,
  }
}
